import * as Sentry from '@sentry/vue'

function safeStringify(value) {
    const seen = new WeakSet()
    try {
        return JSON.stringify(value, (key, current) => {
            if (typeof current === 'object' && current !== null) {
                if (seen.has(current)) {
                    return '[Circular]'
                }
                seen.add(current)
            }
            if (typeof current === 'function') {
                return `[Function:${current.name || 'anonymous'}]`
            }
            return current
        }, 2)
    } catch {
        return String(value)
    }
}

function buildRawErrorSnapshot(error) {

    if (!error) 
        return 'Unknown error'

    if (error && typeof error === 'object') {
        const axiosSnapshot = {
            message: error.message || null,
            code: error.code || null,
            name: error.name || null,
            config: {
                method: error.config?.method || null,
                url: error.config?.url || null,
                baseURL: error.config?.baseURL || null,
                timeout: error.config?.timeout || null,
            },
            response: {
                status: error.response?.status || null,
                statusText: error.response?.statusText || null,
                data: error.response?.data || null,
            },
            request: {
                responseURL: error.request?.responseURL || null,
                readyState: error.request?.readyState || null,
                status: error.request?.status || null,
            }
        }

        return safeStringify(axiosSnapshot)
    }

    return String(error)
}

export function captureApiException(error, fileName, functionName, extra = {}) {

    // Backward-compatible signature support:
    // 1) captureApiException(error, 'File', 'fn', { any: 'extra' })
    // 2) captureApiException(error, { tags: { file, function }, extra })
    let resolvedFileName = fileName
    let resolvedFunctionName = functionName
    let resolvedExtra = extra

    if (fileName && typeof fileName === 'object' && !Array.isArray(fileName)) {
        const options = fileName
        resolvedFileName = options?.tags?.file
        resolvedFunctionName = options?.tags?.function
        resolvedExtra = options?.extra || {}
    }

    const normalizedError = error instanceof Error
        ? error
        : new Error(String(error || 'Unknown error'))
    const rawSnapshot = buildRawErrorSnapshot(error)

    const stackFrame = normalizedError?.stack?.split('\n')?.[1] || ''
    const stackMatch = stackFrame.match(/:(\d+):(\d+)\)?$/)
    const line = stackMatch?.[1] || '0'
    const column = stackMatch?.[2] || '0'
    const release = __APP_SENTRY_RELEASE__

    const resolvedTags = {
        file: resolvedFileName || 'unknown-file',
        function: resolvedFunctionName || 'unknown-function',
        where: `${resolvedFileName || 'unknown-file'}:${line}:${column}`,
        sentry_release: release
    }

    let eventId = null
    Sentry.withScope((scope) => {
        // Scope-level tags are consistently attached across SDK versions.
        scope.setTag('where', resolvedTags.where)

        scope.setExtras({
            ...resolvedExtra,
            error: {
                name: normalizedError.name,
                message: normalizedError.message,
                stack: normalizedError.stack || null,
                raw: rawSnapshot
            },
            sentryTagFile: resolvedTags.file,
            sentryTagFunction: resolvedTags.function,
        })

        eventId = Sentry.captureException(normalizedError)
    })

    return eventId
}
